import React, { useState, useEffect } from 'react';
import { ASSETS } from '../assets/assetMapping';
import { mixpanelService } from '../services/mixpanelService';
import { apiService } from '../services/apiService';
import { getCachedUserIP } from '../utils/ipDetection';
import { FilterState } from '../types/api';

interface SubmitModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  prompt: string;
  filters?: FilterState;
  hasReference?: boolean;
  generationId?: string;
  enhancedPrompt?: string;
  imagesUrl?: string[];
  onError?: (message: string) => void;
}

type ModalState = 'form' | 'success';

const SubmitModal: React.FC<SubmitModalProps> = ({
  isOpen,
  onClose,
  imageUrl,
  prompt,
  filters,
  hasReference = false,
  generationId,
  enhancedPrompt,
  imagesUrl = [],
  onError
}) => {
  const [modalState, setModalState] = useState<ModalState>('form');
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      // Reset to form state when opening
      setModalState('form');
      // Clear form data
      setFormData({ fullName: '', email: '' });
    } else {
      setIsVisible(false);
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const validateForm = (): boolean => {
    if (!formData.fullName.trim()) {
      if (onError) {
        onError('Please enter your full name');
      }
      return false;
    }

    if (!formData.email.trim()) {
      if (onError) {
        onError('Please enter your email address');
      }
      return false;
    }

    if (!emailRegex.test(formData.email)) {
      if (onError) {
        onError('Please enter a valid email address');
      }
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Get user IP
      const userIP = await getCachedUserIP();
      
      // Validate required fields
      if (!generationId) {
        throw new Error('Generation ID is missing');
      }

      // Prepare submit data
      const submitData = {
        userName: formData.fullName,
        email: formData.email,
        generationId: generationId,
        userPrompt: prompt,
        enhancedPrompt: enhancedPrompt || prompt,
        userFilterSelection: filters || {},
        usedImageAnalysis: hasReference || false,
        userIP: userIP,
        timestamp: new Date().toISOString(),
        imagesUrl: imagesUrl.length > 0 ? imagesUrl : []
      };

      // Publish to Slack via backend
      await apiService.publishSubmitToSlack(submitData);

      // Track review-submit event
      if (filters) {
        mixpanelService.trackReviewSubmit(
          formData.fullName,
          formData.email,
          prompt,
          filters
        );
      }

      // Transition to success state
      setModalState('success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to submit request. Please try again.';
      if (onError) {
        onError(errorMessage);
      }
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-[rgba(0,0,0,0.6)]" />
      
      {/* Modal Content */}
      <div className={`relative bg-gradient-to-l from-[#f4fbfe] from-[2.372%] to-[#f6f8ff] to-[99.351%] rounded-[16px] w-full max-w-[460px] transition-all duration-300 ${
        isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
      }`}>
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white hover:bg-opacity-20 transition-all"
          aria-label="Close modal"
        >
          <img 
            src={ASSETS.VECTOR_64_STROKE} 
            alt="Close" 
            className="w-3.5 h-3.5"
          />
        </button>

        {modalState === 'form' ? (
          /* Form State */
          <div className="px-[16px] md:px-[88px] py-[40px] md:py-[56px]">
            {/* Header */}
            <div className="flex flex-col gap-2 items-start text-center mb-8">
              <h2 className="font-['Inter'] font-bold text-[24px] leading-normal text-[#253658] w-full">
                Submit Request
              </h2>
              <p className="font-['Inter'] font-normal text-[14px] leading-[1.4] text-[rgba(37,54,88,0.6)] w-full">
                Please enter your full name and email so we can send you your avatar
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              {/* Full Name Input */}
              <div className="flex flex-col gap-1">
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  placeholder="Full Name"
                  className="bg-white border border-[rgba(37,54,88,0.3)] h-[40px] px-4 py-1.5 rounded-lg font-['Inter'] font-normal text-[16px] leading-[1.4] text-[#253658] placeholder:text-[rgba(37,54,88,0.3)] focus:outline-none focus:border-[#4481fd] transition-colors"
                  disabled={isSubmitting}
                />
              </div>

              {/* Email Input */}
              <div className="flex flex-col gap-1">
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  className="bg-white border border-[rgba(37,54,88,0.3)] h-[40px] px-4 py-1.5 rounded-lg font-['Inter'] font-normal text-[16px] leading-[1.4] text-[#253658] placeholder:text-[rgba(37,54,88,0.3)] focus:outline-none focus:border-[#4481fd] transition-colors"
                  disabled={isSubmitting}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="bg-[#4481fd] hover:bg-blue-600 disabled:bg-blue-400 disabled:cursor-not-allowed h-[40px] px-4 py-2 rounded-[10px] font-['Inter'] font-semibold text-[16px] text-white transition-colors flex items-center justify-center"
              >
                {isSubmitting ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </div>
                ) : (
                  'Submit'
                )}
              </button>
            </form>
          </div>
        ) : (
          /* Success State */
          <div className="px-[88px] py-[56px] flex flex-col items-center gap-8">
            {/* Success Image */}
            <div className="w-[104px] h-[104px]">
              <img 
                src={ASSETS.SUCCESS_CONFIRMATION_IMAGE} 
                alt="Success" 
                className="w-full h-full object-contain"
              />
            </div>

            {/* Success Message */}
            <div className="flex flex-col gap-2 items-start text-center">
              <h2 className="font-['Inter'] font-bold text-[24px] leading-normal text-[#253658] w-full">
                All set — thank you!
              </h2>
              <p className="font-['Inter'] font-normal text-[14px] leading-[1.4] text-[rgba(37,54,88,0.6)] w-full">
                We've received your request and started processing it. We'll send you an email as soon as your avatar is ready.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitModal;

